/**
 * Attendance service
 * Provides helpers for the event_attendance table and related RPC.
 */

import { supabase } from '../supabase';
import type { Database, EventAttendance } from '../types/database';
import type { EventAttendanceWithProfile } from '../types/attendance.types';

type AttendanceViewRow =
  Database['public']['Views']['event_attendance_with_profiles']['Row'];

function mapViewRow(row: AttendanceViewRow): EventAttendanceWithProfile {
  const profile =
    row.full_name || row.email
      ? {
          full_name: row.full_name,
          email: row.email,
          role: row.role,
        }
      : null;

  return {
    id: row.id ?? 0,
    event_id: row.event_id ?? 0,
    user_id: row.user_id ?? '',
    recorded_by: row.recorded_by,
    created_at: row.created_at ?? '',
    profile,
  };
}

export async function getEventAttendance(
  eventId: number
): Promise<EventAttendanceWithProfile[]> {
  const { data, error } = await supabase
    .from('event_attendance_with_profiles')
    .select('*')
    .eq('event_id', eventId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch attendance: ${error.message}`);
  }

  return (data || []).map(mapViewRow);
}

export async function syncEventAttendance(params: {
  eventId: number;
  attendeeIds: string[];
}) {
  const { eventId, attendeeIds } = params;
  const payload = {
    p_event_id: eventId,
    p_attendee_ids: attendeeIds.length ? attendeeIds : null,
  };
  const { data, error } = await supabase.rpc('sync_event_attendance', {
    ...payload,
  });

  if (error) {
    throw new Error(`Failed to sync attendance: ${error.message}`);
  }

  return (data || []) as EventAttendance[];
}

export async function deleteAttendance(attendanceId: number): Promise<void> {
  const { error } = await supabase
    .from('event_attendance')
    .delete()
    .eq('id', attendanceId);

  if (error) {
    throw new Error(`Failed to delete attendance: ${error.message}`);
  }
}

